/**
 * Baut die Zeilen der Buchhaltungs-Tabelle als echte Eltern-Kind-Struktur:
 * die RECHNUNG ist das Elternelement, die Mahnung haengt als Kind darunter.
 *
 * Bis Juli 2026 war es umgekehrt (Mahnung als aufklappbare Elternzeile mit ihren
 * Rechnungen als Kinder) — fachlich verkehrt herum und technisch ueber ein
 * flaches Array mit Marker-Zeilen geloest, was Pagination und Sortierung bricht.
 *
 * Nennt eine Sammelmahnung mehrere Rechnungen, erscheint sie unter JEDER davon.
 * Das ist gewollt (sie mahnt diese Rechnung ja tatsaechlich an); die Kindzeile
 * zeigt deshalb NIE den Mahnbetrag, sondern nur Stufe, Datum und die anteilige
 * Nebenforderung — sonst liest man denselben Betrag N-fach als Forderung.
 */

export type InvoiceRowKind = 'invoice' | 'reminder';

export interface ReminderChild {
  kind: 'reminder';
  id: number;
  parentInvoiceId: number;
  reminder_level: number | null;
  invoice_date: string | null;
  due_date: string | null;
  status: string;
  gross_amount: number | null;
  description: string | null;
  original_file_path: string | null;
  original_file_name: string | null;
  fees_amount: number | null;
  fees_source: string | null;
  fee_dunning: number | null;
  fee_penalty: number | null;
  fee_interest: number | null;
  /** Anteil dieser Rechnung an der Nebenforderung — reine Anzeige, nie gespeichert */
  fee_share: number | null;
  /** Anzahl Rechnungen, die diese Mahnung insgesamt anmahnt (>1 = Sammelmahnung) */
  linked_count: number;
  linked_total: number;
}

export interface InvoiceRow {
  kind: 'invoice';
  id: number;
  [key: string]: any;
  reminders?: ReminderChild[];
}

export type AnyRow = InvoiceRow | ReminderChild;

export function isReminderRow(row: AnyRow): row is ReminderChild {
  return row.kind === 'reminder';
}

/**
 * Wandelt die API-Liste in Tabellenzeilen um. Erwartet, dass die Liste mit
 * `?include=reminders` geladen wurde — die Kinder kommen dann bereits mit,
 * ohne Zusatzrequest je aufgeklappter Zeile.
 */
export function buildInvoiceRows(invoices: any[]): InvoiceRow[] {
  return (invoices || []).map((inv) => ({
    ...inv,
    kind: 'invoice' as const,
    reminders: (inv.reminders || []).map((r: any) => ({
      ...r,
      kind: 'reminder' as const,
      parentInvoiceId: inv.id,
    })),
  }));
}

/**
 * Sub-Row-Zugriff fuer die DataTable. Nur Elternzeilen haben Kinder; ein leeres
 * Array wuerde TanStack einen Expand-Chevron rendern lassen, deshalb undefined.
 */
export function getInvoiceSubRows(row: AnyRow): AnyRow[] | undefined {
  if (isReminderRow(row)) return undefined;
  const kids = row.reminders;
  return kids && kids.length > 0 ? kids : undefined;
}

/**
 * Stabile Zeilen-IDs. Ohne sie sind die Keys Indizes — Auswahl und
 * Aufklapp-Zustand verrutschen dann beim Refetch oder Sortieren.
 */
export function getInvoiceRowId(row: AnyRow, index: number, parent?: { id: string }): string {
  const own = isReminderRow(row) ? `r${row.id}` : `i${row.id}`;
  return parent ? `${parent.id}.${own}` : own;
}
